import { FC, useMemo, useState } from "react";
import { Table } from "antd";
import { ActionButton } from "@hdi-seguros/web-components.action-button";
import { DownloadOutlined, DownSquareOutlined } from "@ant-design/icons";
import { downloadFile } from "../../../shared/utils/downloadFileS3";

interface FormDocument {
    key: string;
    lastModified: string;
    size: string;
    url: string;
    metadata?: {
        branch: string;
        description: string;
    }
}

interface FormsListTableProps {
    documentsList: FormDocument[];
    isLoading: boolean;
}

export const FormsListTable: FC<FormsListTableProps> = ({ documentsList, isLoading }) => {
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(12);

    const [downloaded, setDownloaded] = useState<Set<string>>(new Set());
        
    const handleDownload = async (record: FormDocument, fileName: string) => {
        await downloadFile(record.url, fileName);

        setDownloaded(prev => {
            const updated = new Set(prev);
            updated.add(record.key);
            return updated;
        });
    };

    const extractFileInfo = (key: string) => {
        // Extract file name from the key (remove the prefix path)
        const fileName = key.split('/').pop() || '';

        return { fileName };
    };

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('es-AR', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
        });
    };

    const HEADERS_FORMS_TABLE = useMemo(() => [
        {
            title: '',
            key: 'download',
            width: 80,
            align: 'center' as const,
            render: (record: FormDocument) => {
                const { fileName } = extractFileInfo(record.key);
                const isDownloaded = downloaded.has(record.key);

                return (
                    <ActionButton
                        icon={isDownloaded ? <DownSquareOutlined /> : <DownloadOutlined />}
                        onClick={() => !isDownloaded && handleDownload(record, fileName)}
                        label={isDownloaded ? "Descargado" : "Descargar"}
                        color="secondaryDefault"
                        type={isDownloaded ? "text" : "secondary"}
                    />
                );
            }
        },
        {
            title: 'RAMO',
            key: 'ramo',
            render: (record: FormDocument) => {
                return record.metadata?.branch || '-';
            }
        },
        {
            title: 'DESCRIPCIÓN',
            key: 'descripcion',
            render: (record: FormDocument) => {
                return record.metadata?.description || '-';
            }
        },
        {
            title: 'NOMBRE',
            key: 'nombre',
            render: (record: FormDocument) => {
                const { fileName } = extractFileInfo(record.key);
                return fileName;
            }
        },
        {
            title: 'FECHA DE PUBLICACIÓN',
            key: 'fecha',
            render: (record: FormDocument) => formatDate(record.lastModified)
        },
    ], [downloaded]);

    return (
        <Table
            loading={isLoading}
            bordered
            className='w-100'
            columns={HEADERS_FORMS_TABLE}
            dataSource={documentsList || []}
            pagination={{
                current: currentPage,
                pageSize: pageSize,
                showTotal: (total) =>
                    total === 1 ? 'Un documento' : `${total} documentos`,
                total: documentsList?.length || 0,
                showSizeChanger: true,
                pageSizeOptions: ['12', '24', '48', '96'],
                onChange: (page, pageSize) => {
                    setCurrentPage(page);
                    setPageSize(pageSize);
                },
            }}
            rowKey={(record) => record.key}
        />
    );
}